import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';

/** ms the 3D layer stays faded out while the slider swaps figures. */
const SWITCH_MS = 320;

/**
 * <tmc-hero-depth>: one or more figures (slides). Each is a product photo + depth map rendered in WebGL
 * so the figure shifts in perspective and catches a light sweep. One WebGL canvas serves every slide
 * (textures are swapped), so more slides cost a photo and a depth map each, not another renderer.
 * The first slide's poster <img> is the LCP element. "See the back" works in every tier.
 *
 * Slider: the next figure arrives when the current dot's progress bar finishes (CSS animation, so pausing
 * is just animation-play-state). Paused while the visitor steers the figure, hovers it, focuses inside the
 * words, views the back, or the hero is off screen / the tab is hidden; it stops once the visitor picks a
 * figure from the bars. No auto-advance for reduced motion or Motion: Off; the bars still work.
 *
 * @typedef {{ media: HTMLElement, canvas: HTMLCanvasElement, controls?: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class TmcHeroDepth extends Component {
  requiredRefs = ['media', 'canvas'];

  /** @type {AbortController | null} */
  #abort = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {DepthRenderer | null} */
  #renderer = null;
  #current = 0;
  /** Why the slider is paused right now (empty = playing). @type {Set<string>} */
  #holds = new Set();
  /** @type {Map<number, Promise<[HTMLImageElement, HTMLImageElement] | null>>} */
  #textures = new Map();
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #switchTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    this.#setUpTurn();
    this.#setUpSlider();
    // Owner choice (DECISIONS #92): the cheap motion runs from load. data-awake starts the CSS drift / light
    // sweep (transform + opacity, GPU-composited) and the slider clock at once. The WebGL renderer, the
    // expensive part, still waits for the first scroll / touch / pointer move / key in #start().
    this.dataset.awake = '';
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#renderer?.dispose();
    this.#renderer = null;
    clearTimeout(this.#switchTimer);
  }

  /** @returns {HTMLElement[]} the picture layer of every slide */
  #slides() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.tmc-hero__slide')]);
  }

  #setUpTurn() {
    const signal = this.#abort?.signal;
    for (const turn of /** @type {NodeListOf<HTMLButtonElement>} */ (this.querySelectorAll('[data-turn]'))) turn.hidden = false;
    this.addEventListener(
      'click',
      (event) => {
        const turn = /** @type {HTMLElement} */ (event.target).closest?.('[data-turn]');
        if (!(turn instanceof HTMLButtonElement)) return;
        this.#setView(this.dataset.view !== 'back' ? 'back' : 'front');
      },
      { signal }
    );
  }

  /** @param {'front' | 'back'} view */
  #setView(view) {
    this.dataset.view = view;
    for (const turn of /** @type {NodeListOf<HTMLButtonElement>} */ (this.querySelectorAll('[data-turn]'))) {
      turn.setAttribute('aria-pressed', String(view === 'back'));
      turn.textContent = view === 'back' ? turn.dataset.labelBack ?? '' : turn.dataset.labelFront ?? '';
    }
    this.#renderer?.setPaused(view === 'back');
    this.#hold('back', view === 'back');
  }

  #setUpSlider() {
    const { controls, media } = this.refs;
    if (!controls || this.#slides().length < 2) return;
    const signal = this.#abort?.signal;
    controls.hidden = false;

    controls.addEventListener(
      'click',
      (event) => {
        const dot = /** @type {HTMLElement} */ (event.target).closest?.('[data-goto]');
        if (!(dot instanceof HTMLElement)) return;
        // Picking a figure hands control to the visitor: auto-advance stops.
        this.#hold('chosen', true);
        this.#goTo(Number(dot.dataset.goto));
      },
      { signal }
    );
    // The current dot's progress bar finishing is the clock.
    controls.addEventListener(
      'animationend',
      (event) => {
        if (event.animationName === 'tmc-hero-progress') this.#goTo((this.#current + 1) % this.#slides().length);
      },
      { signal }
    );
    // Visitor in control: steering (mouse over the figure, or a finger on it) or reading/tabbing the words.
    media.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && this.#hold('steer', true), { signal });
    media.addEventListener('pointerleave', (event) => event.pointerType === 'mouse' && this.#hold('steer', false), { signal });
    media.addEventListener('pointerdown', (event) => event.pointerType !== 'mouse' && this.#hold('touch', true), { signal });
    window.addEventListener('pointerup', () => this.#hold('touch', false), { signal });
    window.addEventListener('pointercancel', () => this.#hold('touch', false), { signal });
    this.addEventListener('focusin', () => this.#hold('focus', true), { signal });
    this.addEventListener('focusout', () => this.#hold('focus', false), { signal });
    document.addEventListener('visibilitychange', () => this.#hold('hidden', document.hidden), { signal });
    const onScreen = new IntersectionObserver(([entry]) => this.#hold('offscreen', !entry?.isIntersecting));
    onScreen.observe(this);
    signal?.addEventListener('abort', () => onScreen.disconnect());
  }

  /** @param {string} reason @param {boolean} on */
  #hold(reason, on) {
    if (on) this.#holds.add(reason);
    else this.#holds.delete(reason);
    this.toggleAttribute('data-paused', this.#holds.size > 0);
  }

  /** @param {number} index */
  #goTo(index) {
    if (index === this.#current || !this.#slides()[index]) return;
    this.#current = index;
    if (this.dataset.view === 'back') this.#setView('front');

    for (const element of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.tmc-hero__slide, .tmc-hero__slide-copy'))) {
      const match = Number(element.dataset.slide) === index;
      element.toggleAttribute('data-current', match);
      if (element.classList.contains('tmc-hero__slide-copy')) element.inert = !match;
    }
    for (const dot of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-goto]'))) {
      dot.setAttribute('aria-current', String(Number(dot.dataset.goto) === index));
    }
    this.#swapFigure();
  }

  /** Fades the 3D layer out, swaps in the current slide's textures, fades back in with a fresh shine. */
  async #swapFigure() {
    const renderer = this.#renderer;
    if (!renderer) return;
    const index = this.#current;
    this.dataset.switching = '';
    clearTimeout(this.#switchTimer);
    const wait = new Promise((resolve) => (this.#switchTimer = setTimeout(resolve, SWITCH_MS)));
    const [textures] = await Promise.all([this.#texturesFor(index), wait]);
    if (index !== this.#current || renderer !== this.#renderer) return;
    if (textures) {
      renderer.swap(textures[0], textures[1]);
      delete this.dataset.flat;
    } else {
      this.dataset.flat = ''; // no depth map for this figure: the photo shows on its own
    }
    delete this.dataset.switching;
  }

  /** @param {number} index */
  #texturesFor(index) {
    let pending = this.#textures.get(index);
    if (!pending) {
      const slide = this.#slides()[index];
      const poster = /** @type {HTMLImageElement | null} */ (slide?.querySelector('img.tmc-hero__poster') ?? null);
      const depth = slide?.dataset.depthSrc;
      pending =
        poster && depth
          ? Promise.all([loadTexture(poster.currentSrc || poster.src), loadTexture(depth)]).catch(() => null)
          : Promise.resolve(null);
      this.#textures.set(index, pending);
    }
    return pending;
  }

  async #start() {
    const motion = await getMotion();
    const tier = motion.tier(this.dataset.motion);
    if (tier === 'full' || tier === 'lite') {
      this.#followScroll();
      if (this.#slides().length > 1) this.dataset.autoplay = '';
    }
    if (tier !== 'full') return;
    if (!supportsWebGL()) return;

    // Owner choice (DECISIONS #96): the 3D tilt + shine starts by itself about a second after the page has
    // loaded, on devices with a real graphics chip. Devices that would draw WebGL in software (no GPU: very
    // cheap phones, headless test browsers) keep the old rule and wait for the first move, scroll or touch.
    await afterLoad();
    const interaction = firstInteraction(/** @type {AbortSignal} */ (this.#abort?.signal));
    if (hasHardwareGPU()) {
      await Promise.race([interaction, new Promise((resolve) => setTimeout(resolve, AUTO_START_MS))]);
    } else {
      await interaction;
    }
    if (!this.isConnected) return;
    requestIdleCallback(() => this.#initRenderer());
  }

  /** Parallax driver: --tmc-hero-p goes 0 → 1 as the hero scrolls away; CSS moves the layers from it. */
  #followScroll() {
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = this.getBoundingClientRect();
      if (rect.bottom < 0) return;
      const progress = Math.max(0, Math.min(1, -rect.top / Math.max(rect.height, 1)));
      this.style.setProperty('--tmc-hero-p', progress.toFixed(4));
    };
    // Capture on document: on desktop Horizon scrolls .page-wrapper, not the window, and scroll does not bubble.
    document.addEventListener(
      'scroll',
      () => {
        if (!frame) frame = requestAnimationFrame(update);
      },
      { passive: true, capture: true, signal: this.#abort?.signal }
    );
  }

  async #initRenderer() {
    if (this.#renderer) return;
    const { media, canvas } = this.refs;
    const index = this.#current;
    const textures = await this.#texturesFor(index);
    if (!this.isConnected || this.#renderer) return;
    if (!textures) {
      // This figure has no depth map. Start the renderer when the slider reaches one that does.
      this.dataset.flat = '';
      const later = this.#slides().findIndex((slide, i) => i !== index && slide.dataset.depthSrc);
      if (later >= 0) this.addEventListener('transitionend', () => this.#initRenderer(), { once: true, signal: this.#abort?.signal });
      return;
    }
    try {
      this.#renderer = new DepthRenderer(canvas, textures[0], textures[1], {
        strength: Number(this.dataset.strength ?? 6) * 0.005,
        auto: this.dataset.auto !== undefined,
        box: media,
        signal: /** @type {AbortSignal} */ (this.#abort?.signal),
        onFirstFrame: () => (this.dataset.glReady = ''),
        onLost: () => delete this.dataset.glReady,
      });
      delete this.dataset.flat;
      if (index !== this.#current) this.#swapFigure();

      this.#observer = new IntersectionObserver(([entry]) => this.#renderer?.setVisible(Boolean(entry?.isIntersecting)));
      this.#observer.observe(this);
      // Warm the other figures' textures while idle, so slide changes are instant.
      requestIdleCallback(() => this.#slides().forEach((_, i) => this.#texturesFor(i)));
    } catch {
      // Any failure leaves the posters in place, which is the intended fallback.
    }
  }
}

const SWEEP_SECONDS = 2.6;
/** Auto start of the 3D effect after load (ms), on hardware-accelerated devices. */
const AUTO_START_MS = 900;
/** Auto mode: seconds of no input before the motion resumes, seconds between shine sweeps, and seconds per full sway. */
const AUTO_RESUME = 1.5;
const AUTO_SWEEP_EVERY = 3.6;
const AUTO_SWAY_SECONDS = 6;

const VERTEX = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAGMENT = `
precision mediump float;
varying vec2 vUv;
uniform sampler2D uImage;
uniform sampler2D uDepth;
uniform vec2 uCover;
uniform vec2 uOffset;
uniform vec2 uPointer;
uniform vec2 uTexel;
uniform float uStrength;
uniform float uFocus;
uniform float uSweep;

void main() {
  vec2 uv = vUv * uCover + uOffset;
  vec2 dir = uPointer * uStrength;

  // Two-step parallax: sample depth, shift, refine.
  float d = texture2D(uDepth, uv).r;
  vec2 p = uv + dir * (d - uFocus);
  d = texture2D(uDepth, p).r;
  p = clamp(uv + dir * (d - uFocus), 0.0, 1.0);
  vec3 col = texture2D(uImage, p).rgb;

  // Surface normal from the depth gradient, for rim light on the armour edges.
  float dx = texture2D(uDepth, p + vec2(uTexel.x, 0.0)).r - texture2D(uDepth, p - vec2(uTexel.x, 0.0)).r;
  float dy = texture2D(uDepth, p + vec2(0.0, uTexel.y)).r - texture2D(uDepth, p - vec2(0.0, uTexel.y)).r;
  vec3 n = normalize(vec3(-dx * 6.0, -dy * 6.0, 1.0));
  vec3 l = normalize(vec3(uPointer.x * 0.8 - 0.3, 0.5 + uPointer.y * 0.3, 0.7));
  float diffuse = clamp(dot(n, l), 0.0, 1.0);

  float figure = smoothstep(0.35, 0.55, d);
  float luma = dot(col, vec3(0.299, 0.587, 0.114));
  float band = smoothstep(0.18, 0.0, abs((p.x + p.y * 0.6) - uSweep));
  float sheen = band * figure * pow(luma, 1.4) * 0.8;
  float rim = (1.0 - n.z) * figure * 0.35;

  vec3 lit = col * (0.92 + 0.12 * diffuse * figure) + vec3(1.0, 0.92, 0.82) * (sheen + rim * diffuse);
  gl_FragColor = vec4(lit, 1.0);
}`;

/**
 * Minimal WebGL renderer for one image + depth map. No libraries.
 */
class DepthRenderer {
  /** @type {WebGLRenderingContext} */
  #gl;
  /** @type {WebGLProgram} */
  #program;
  /** @type {WebGLTexture[]} */
  #textures = [];
  /** @type {WebGLBuffer | null} */
  #buffer = null;
  /** @type {Record<string, WebGLUniformLocation | null>} */
  #u = {};
  #canvas;
  #box;
  #imageAspect;
  #options;
  #raf = 0;
  #last = 0;
  #time = 0;
  #visible = false;
  #paused = false;
  #firstFrame = true;
  #sweepStart = 0; // the first sweep plays as the canvas fades in
  /** @type {{ x: number, y: number, startX: number } | null} */
  #drag = null;
  #pointer = { x: 0, y: 0 };
  #target = { x: 0, y: 0 };
  #fine = matchMedia('(pointer: fine)').matches;
  /** Renderer time of the visitor's last pointer move or drag; auto motion waits AUTO_RESUME after it. */
  #lastInput = -Infinity;
  #skip = false;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /**
   * @param {HTMLCanvasElement} canvas
   * @param {HTMLImageElement} image
   * @param {HTMLImageElement} depth
   * @param {{ strength: number, auto: boolean, box: HTMLElement, signal: AbortSignal, onFirstFrame: () => void, onLost: () => void }} options
   */
  constructor(canvas, image, depth, options) {
    const gl = canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'high-performance' });
    if (!gl) throw new Error('WebGL unavailable');
    this.#gl = gl;
    this.#canvas = canvas;
    this.#box = options.box;
    this.#options = options;
    this.#imageAspect = image.naturalWidth / image.naturalHeight;

    this.#program = createProgram(gl, VERTEX, FRAGMENT);
    gl.useProgram(this.#program);
    for (const name of ['uImage', 'uDepth', 'uCover', 'uOffset', 'uPointer', 'uTexel', 'uStrength', 'uFocus', 'uSweep']) {
      this.#u[name] = gl.getUniformLocation(this.#program, name);
    }

    this.#buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.#buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(this.#program, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    this.#textures.push(createTexture(gl, 0, image), createTexture(gl, 1, depth));
    gl.uniform1i(this.#u.uImage ?? null, 0);
    gl.uniform1i(this.#u.uDepth ?? null, 1);
    gl.uniform2f(this.#u.uTexel ?? null, 1 / depth.naturalWidth, 1 / depth.naturalHeight);
    gl.uniform1f(this.#u.uStrength ?? null, options.strength);
    gl.uniform1f(this.#u.uFocus ?? null, 0.7);

    const { signal } = options;
    if (this.#fine) {
      window.addEventListener('pointermove', (event) => this.#onPointer(event), { passive: true, signal });
      this.#box.addEventListener('pointerenter', () => this.#sweep(), { signal });
    } else {
      // Touch: a sideways drag on the figure turns it (the stage uses touch-action: pan-y, so vertical
      // swipes still scroll the page); scrolling turns it gently too.
      document.addEventListener('scroll', () => this.#schedule(), { passive: true, capture: true, signal });
      this.#box.addEventListener('pointerdown', (event) => this.#dragStart(event), { signal });
      window.addEventListener('pointermove', (event) => this.#dragMove(event), { passive: true, signal });
      window.addEventListener('pointerup', () => this.#dragEnd(), { signal });
      window.addEventListener('pointercancel', () => this.#dragEnd(), { signal });
    }
    canvas.addEventListener(
      'webglcontextlost',
      (event) => {
        event.preventDefault();
        cancelAnimationFrame(this.#raf);
        options.onLost();
      },
      { signal }
    );
    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(this.#box);
    document.addEventListener('visibilitychange', () => this.#schedule(), { signal });
    this.#resize();
  }

  /**
   * Replaces the photo and depth textures (slider) and plays a fresh light sweep.
   * @param {HTMLImageElement} image
   * @param {HTMLImageElement} depth
   */
  swap(image, depth) {
    const gl = this.#gl;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.#textures[0] ?? null);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, image);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.#textures[1] ?? null);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, depth);
    gl.uniform2f(this.#u.uTexel ?? null, 1 / depth.naturalWidth, 1 / depth.naturalHeight);
    this.#imageAspect = image.naturalWidth / image.naturalHeight;
    this.#sweepStart = this.#time;
    this.#resize();
  }

  /** @param {boolean} visible */
  setVisible(visible) {
    this.#visible = visible;
    this.#schedule();
  }

  /** @param {boolean} paused */
  setPaused(paused) {
    this.#paused = paused;
    this.#schedule();
  }

  dispose() {
    cancelAnimationFrame(this.#raf);
    this.#resizeObserver?.disconnect();
    const gl = this.#gl;
    for (const texture of this.#textures) gl.deleteTexture(texture);
    gl.deleteBuffer(this.#buffer);
    gl.deleteProgram(this.#program);
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  }

  /** @param {PointerEvent} event */
  #onPointer(event) {
    this.#lastInput = this.#time;
    const rect = this.#box.getBoundingClientRect();
    const x = ((event.clientX - (rect.left + rect.width / 2)) / (rect.width / 2)) * 0.8;
    const y = ((event.clientY - (rect.top + rect.height / 2)) / (rect.height / 2)) * 0.5;
    this.#target.x = Math.max(-1, Math.min(1, x));
    this.#target.y = Math.max(-1, Math.min(1, -y));
    this.#schedule();
  }

  /** @param {PointerEvent} event */
  #dragStart(event) {
    this.#drag = { x: event.clientX, y: event.clientY, startX: this.#target.x };
    this.#lastInput = this.#time;
  }

  /** @param {PointerEvent} event */
  #dragMove(event) {
    if (!this.#drag) return;
    this.#lastInput = this.#time;
    const dx = (event.clientX - this.#drag.x) / Math.max(this.#box.clientWidth, 1);
    const dy = (event.clientY - this.#drag.y) / Math.max(this.#box.clientHeight, 1);
    this.#target.x = Math.max(-1, Math.min(1, this.#drag.startX + dx * 2.4));
    this.#target.y = Math.max(-0.6, Math.min(0.6, -dy * 1.2));
    this.#schedule();
  }

  #dragEnd() {
    if (!this.#drag) return;
    this.#drag = null;
    this.#schedule(); // eases back to the scroll-driven angle
  }

  /** Runs one light sweep across the figure. */
  #sweep() {
    this.#sweepStart = this.#time;
    this.#schedule();
  }

  #resize() {
    const rect = this.#box.getBoundingClientRect();
    const ratio = Math.min(window.devicePixelRatio || 1, this.#fine ? 2 : 1.5);
    this.#canvas.width = Math.max(1, Math.round(rect.width * ratio));
    this.#canvas.height = Math.max(1, Math.round(rect.height * ratio));
    this.#gl.viewport(0, 0, this.#canvas.width, this.#canvas.height);

    // Match the poster's object-fit: cover, centred.
    const boxAspect = rect.width / Math.max(rect.height, 1);
    if (boxAspect > this.#imageAspect) {
      const scale = this.#imageAspect / boxAspect;
      this.#gl.uniform2f(this.#u.uCover ?? null, 1, scale);
      this.#gl.uniform2f(this.#u.uOffset ?? null, 0, (1 - scale) / 2);
    } else {
      const scale = boxAspect / this.#imageAspect;
      this.#gl.uniform2f(this.#u.uCover ?? null, scale, 1);
      this.#gl.uniform2f(this.#u.uOffset ?? null, (1 - scale) / 2, 0);
    }
    this.#schedule();
  }

  /** Starts the render loop if it isn't running. The loop stops by itself once nothing is moving. */
  #schedule() {
    if (!this.#visible || this.#paused || document.hidden) {
      cancelAnimationFrame(this.#raf);
      this.#raf = 0;
      return;
    }
    if (this.#raf) return;
    this.#last = performance.now();
    this.#raf = requestAnimationFrame((now) => this.#frame(now));
  }

  /** @param {number} now */
  #frame(now) {
    // The auto loop never runs off screen, in a background tab, or while the back view is showing.
    if (!this.#visible || this.#paused || document.hidden) {
      this.#raf = 0;
      return;
    }
    const delta = Math.min((now - this.#last) / 1000, 0.1);
    this.#last = now;
    this.#time += delta;

    const auto = this.#options.auto && !this.#drag && this.#time - this.#lastInput > AUTO_RESUME;
    if (auto) {
      // Nobody is steering: a slow figure-of-eight sway, and a shine across the armour every few seconds.
      const phase = (this.#time / AUTO_SWAY_SECONDS) * Math.PI * 2;
      this.#target.x = Math.sin(phase) * 1.15;
      this.#target.y = Math.sin(phase * 2) * 0.32;
      if (this.#time - this.#sweepStart > AUTO_SWEEP_EVERY) this.#sweepStart = this.#time;
    } else if (!this.#fine && !this.#drag) {
      // Touch, not dragging: the figure turns as the hero scrolls away.
      const rect = this.#box.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, -rect.top / Math.max(rect.height, 1)));
      this.#target.x = progress * 0.9;
      this.#target.y = 0;
    }

    // Frame-rate independent easing toward the target.
    const ease = 1 - Math.pow(1 - 0.06, delta * 60);
    this.#pointer.x += (this.#target.x - this.#pointer.x) * ease;
    this.#pointer.y += (this.#target.y - this.#pointer.y) * ease;

    // Phones draw every other frame while auto-moving (~30fps): same look, half the battery.
    this.#skip = auto && !this.#fine && !this.#skip;
    if (this.#skip && !this.#firstFrame) {
      this.#raf = requestAnimationFrame((next) => this.#frame(next));
      return;
    }

    const sweepProgress = (this.#time - this.#sweepStart) / SWEEP_SECONDS;
    const sweeping = sweepProgress >= 0 && sweepProgress <= 1;

    const gl = this.#gl;
    gl.uniform2f(this.#u.uPointer ?? null, this.#pointer.x, this.#pointer.y);
    gl.uniform1f(this.#u.uSweep ?? null, sweeping ? sweepProgress * 3.2 - 1 : -5);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    if (this.#firstFrame) {
      this.#firstFrame = false;
      this.#options.onFirstFrame();
    }

    const settling = Math.abs(this.#target.x - this.#pointer.x) + Math.abs(this.#target.y - this.#pointer.y) > 0.0008;
    this.#raf = auto || sweeping || settling ? requestAnimationFrame((next) => this.#frame(next)) : 0;
  }
}

/**
 * @param {WebGLRenderingContext} gl
 * @param {string} vertexSource
 * @param {string} fragmentSource
 */
function createProgram(gl, vertexSource, fragmentSource) {
  const program = gl.createProgram();
  if (!program) throw new Error('No program');
  for (const [type, source] of /** @type {const} */ ([
    [gl.VERTEX_SHADER, vertexSource],
    [gl.FRAGMENT_SHADER, fragmentSource],
  ])) {
    const shader = gl.createShader(type);
    if (!shader) throw new Error('No shader');
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) ?? 'Shader error');
    gl.attachShader(program, shader);
  }
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) ?? 'Link error');
  return program;
}

/**
 * @param {WebGLRenderingContext} gl
 * @param {number} unit
 * @param {HTMLImageElement} image
 */
function createTexture(gl, unit, image) {
  const texture = gl.createTexture();
  if (!texture) throw new Error('No texture');
  gl.activeTexture(gl.TEXTURE0 + unit);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, image);
  return texture;
}

/**
 * Loads an image for use as a texture. Cross-origin URLs are requested with CORS.
 * @param {string} src
 * @returns {Promise<HTMLImageElement>}
 */
async function loadTexture(src) {
  const image = new Image();
  const url = new URL(src, location.href);
  if (url.origin !== location.origin) image.crossOrigin = 'anonymous';
  image.decoding = 'async';
  image.src = url.href;
  await image.decode();
  return image;
}

function supportsWebGL() {
  try {
    return Boolean(document.createElement('canvas').getContext('webgl'));
  } catch {
    return false;
  }
}

/**
 * Resolves on the visitor's first pointer move, touch, scroll or key press.
 * @param {AbortSignal} signal
 */
function firstInteraction(signal) {
  return new Promise((resolve) => {
    const options = { once: true, passive: true, signal };
    for (const type of ['pointermove', 'pointerdown', 'touchstart', 'scroll', 'keydown']) {
      (type === 'scroll' ? document : window).addEventListener(type, () => resolve(undefined), { ...options, capture: true });
    }
  });
}

/**
 * True when WebGL runs on a real graphics chip. Software renderers (SwiftShader, llvmpipe, Microsoft Basic
 * Render) mean every frame costs the main processor, so the 3D effect waits for the visitor instead.
 * @returns {boolean}
 */
function hasHardwareGPU() {
  try {
    const gl = document.createElement('canvas').getContext('webgl');
    if (!gl) return false;
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = String(info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return !/swiftshader|llvmpipe|software|basic render/i.test(renderer);
  } catch {
    return false;
  }
}

/** Resolves after the window load event. */
function afterLoad() {
  if (document.readyState === 'complete') return Promise.resolve();
  return new Promise((resolve) => window.addEventListener('load', () => resolve(undefined), { once: true }));
}

/** @returns {Promise<any>} window.TMC.motion, once the runtime has run */
function getMotion() {
  const ready = /** @type {any} */ (window).TMC?.motion;
  if (ready) return Promise.resolve(ready);
  return new Promise((resolve) => {
    window.addEventListener('tmc:motion-ready', () => resolve(/** @type {any} */ (window).TMC.motion), { once: true });
  });
}

if (!customElements.get('tmc-hero-depth')) customElements.define('tmc-hero-depth', TmcHeroDepth);
